import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { Display, setDisplay } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { ClassManager, FLEX_CONTAINER_COLUMN, FLEX_ITEM, POSITION } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { CjsFilePicker, FilePickerOptions, defaultFilePickerOptions } from "../filePicker/filePicker";
import { I18nType as I18nFilePicker } from "../filePicker/i18n/i18n";
import { I18nType as I18nImagePicker, i18n } from "./i18n/i18n";

/** Options for ImagePicker */
export interface ImagePickerOptions extends FilePickerOptions {

    /** Recommended size for the picture */
    recommendedPictureSize: string;

    /** 
     * Add custom classes to rendered image
     */
    cssClasses: string[];
}

/** Options for ImagePicker */
export const defaultImagePickerOptions: ImagePickerOptions = {
    ...defaultFilePickerOptions,

    /** Title */
    pickerTitle: "Upload image",

    /** Font Awesome icon in Drag&Drop zone*/
    icon: "far fa-image",

    /** 
     * Files type accepted by input
     * @see https://developer.mozilla.org/fr/docs/Web/HTML/Element/Input/file#identifiants_de_type_de_fichier
     */
    accepts: ["image/*"],

    /** Recommended size for the picture */
    recommendedPictureSize: "Prefer a picture of 800x400 px",

    /** Add custom class to rendered image. */
    cssClasses: []
};

@CustomElement({
    name: "cjs-image-picker",
    style: require("./imagePicker.scss"),// no need to add filePicker.scss, it is added from the FilePicker decorator
    template: require("./imagePicker.html"),
    elementCssClasses: [FLEX_CONTAINER_COLUMN, FLEX_ITEM, POSITION.RELATIVE, "cjs-file-picker"],
    i18n // no need to add filePicker.i18n, it is added from the FilePicker decorator
})
export class CjsImagePicker extends CjsFilePicker<I18nFilePicker & I18nImagePicker> {

    /** components */
    @TypedHtmlElements({ ensureLoadedClasses: [CjsImagePicker] })
    public override readonly components!: {
        filePickerTitle: HTMLParagraphElement,
        filePickerIcon: HTMLElement,
        filePickerInput: HTMLInputElement,
        filePickerMultipleError: HTMLParagraphElement,
        filePickerSizeError: HTMLParagraphElement,
        filePickerExtensionError: HTMLParagraphElement,
        imagePickerImageDisplay: HTMLImageElement,
        resetPictureButton: HTMLButtonElement,
        filePickerOptions: HTMLDivElement
    };

    @TypedWebComponentOptions(defaultImagePickerOptions)
    public override readonly options!: WebComponentOptions<ImagePickerOptions>;

    /** Store added css classes from options, use to clean imagePickerDisplay classes when refreshing component display */
    private _classManager = new ClassManager(this.components.imagePickerImageDisplay);

    constructor(options?: Partial<ImagePickerOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(options, customElementOptions);
    }

    /**
     * Initialize components, input components and callbacks after the element is added to the dom and _components is ready
     */
    public override init(): void {
        super.init();
        this.components.resetPictureButton.addEventListener("click", (e) => {
            if (this.disabled) { return; }// Don't allow click callback if component is disabled
            e.stopPropagation();
            this._onResetPictureButtonClick();
        });
        this.valueChangeEvent.on(() => this._updateImagePickerDisplay());
        this.ariaLabel = this.i18n.ariaLabel;
        (this.components.resetPictureButton.children[0] as HTMLElement).ariaLabel = this.i18n.cancelBtn;
    }

    /** Refresh component display */
    public override refresh(): void {
        // ! The imagePicker can only manage one picture at a time, it is not an option
        this.options.setWithoutRefresh({ enableMultiple: false });

        super.refresh();

        setDisplay(this.components.resetPictureButton, Display.NONE);
        setDisplay(this.components.imagePickerImageDisplay, Display.NONE);
        setDisplay(this.components.filePickerOptions, Display.FLEX);
        setDisplay(this.components.filePickerTitle, Display.NONE);
        this.components.imagePickerImageDisplay.setAttribute("src", "");

        if (this.components.filePickerIcon.style.display === Display.NONE) {
            setDisplay(this.components.filePickerIcon, Display.INHERIT);
        }
    }

    /**
     * Returns the current image
     */
    public getImage(): File | undefined {
        return super.getFiles()[0];
    }

    /**
     * Set the picker image
     * @param image 
     */
    public setImage(image: File): void {
        super.setFiles([image]);
        this._updateImagePickerDisplay();
    }

    /**
     * Set the src of imagePickerImageDisplay
     * @param link 
     */
    public setImageWithLink(link: string): void {
        this.components.imagePickerImageDisplay.src = link;
        setDisplay(this.components.resetPictureButton, Display.BLOCK);
        setDisplay(this.components.filePickerIcon, Display.NONE);
        setDisplay(this.components.filePickerOptions, Display.NONE);
        setDisplay(this.components.imagePickerImageDisplay, Display.BLOCK);
        setDisplay(this.components.filePickerTitle, Display.NONE);
    }


    /** Remove the image previously displayed on the image picker */
    public cleanDisplay(p?: { doNotDispatchEvent?: boolean }): void {
        super.emptyFiles(p);
        this.refresh();
    }


    /**
     * Updates the image displayed on the image picker
     */
    private _updateImagePickerDisplay(): void {
        if (this.disabled) { return; }// Don't allow update of imagePicker if component is disabled

        const image = this.getImage();
        if (image) {
            this._classManager.set(...this.options.cssClasses ?? []);

            setDisplay(this.components.imagePickerImageDisplay, Display.BLOCK);
            setDisplay(this.components.filePickerTitle, Display.NONE);

            const fileReader = new FileReader();

            // Load file content callback
            fileReader.onload = (event: ProgressEvent<FileReader>) => {
                this.components.imagePickerImageDisplay.setAttribute("src", event.target?.result as string);

                if (this.components.filePickerIcon.style.display !== Display.NONE) {
                    setDisplay(this.components.resetPictureButton, Display.BLOCK);
                    setDisplay(this.components.filePickerIcon, Display.NONE);
                    setDisplay(this.components.filePickerOptions, Display.NONE);

                }
            };

            // Start file content load
            fileReader.readAsDataURL(image);
        }
    }

    /** Reset the main picture of the post */
    private _onResetPictureButtonClick(): void {
        if (!this._value) {
            return;
        }
        this.cleanDisplay();
    }

    /**
     * Get options string to display in DOM
     */
    protected override _getOptionsString(): string {
        return super._getOptionsString() + (this.options.recommendedPictureSize.length > 0 ? `<br> ${this.options.recommendedPictureSize}` : "");
    }
}
